import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { RouterModule } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { DropdownModule } from 'primeng/dropdown';
import { InputTextModule } from 'primeng/inputtext';
import { DialogModule } from 'primeng/dialog';
import {
  AutoCompleteModule,
  AutoCompleteSelectEvent,
} from 'primeng/autocomplete';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, switchMap } from 'rxjs/operators';
import { JobCardComponent } from '../../components/job-card/job-card.component';
import { MockJobService, Job } from '../../services-mock/mock-job.service';
import { AISummaryComponent } from '../../components/ai-summary/ai-summary.component';
import { MockFeedService } from '../../services-mock/mock-feed.service';

interface CategoryStats {
  name: string;
  count: number;
}

@Component({
  selector: 'app-jobs',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    RouterModule,
    ButtonModule,
    DropdownModule,
    InputTextModule,
    DialogModule,
    AutoCompleteModule,
    JobCardComponent,
    AISummaryComponent,
  ],
  template: `
    <div class="jobs-container">
      <div class="jobs-header">
        <!-- Search - Full Width -->
        <div class="search-container-full">
          <p-autoComplete
            [(ngModel)]="searchQuery"
            [suggestions]="searchSuggestions"
            (completeMethod)="searchAutocomplete($event)"
            (onSelect)="onSearchSelect($event)"
            (ngModelChange)="onSearchInput()"
            placeholder="Search jobs by title, company, or location..."
            [dropdown]="true"
            [minLength]="2"
            class="search-autocomplete"
          ></p-autoComplete>
        </div>

        <!-- Categories Row with Recommended Dropdown -->
        <div class="categories-row">
          <!-- Mobile: Dropdown Select -->
          <div class="category-select-mobile">
            <p-dropdown
              [options]="categoryDropdownOptions"
              [(ngModel)]="selectedCategoryOption"
              placeholder="Select a category"
              [showClear]="true"
              [appendTo]="'body'"
              optionLabel="label"
              optionValue="value"
              (ngModelChange)="onCategorySelectChange($event)"
              class="category-dropdown"
            ></p-dropdown>
          </div>

          <!-- Desktop: Top 3 Categories + All Categories Button -->
          <div class="category-boxes-desktop">
            <div
              *ngFor="let category of top3Categories"
              class="category-box"
              [class.active]="selectedCategory === category.name"
              (click)="onCategoryClick(category.name)"
            >
              <div class="category-name">{{ category.name }}</div>
              <div class="category-count">
                <i class="pi pi-briefcase"></i>
                {{ category.count }}
              </div>
            </div>
            <button
              pButton
              type="button"
              label="All Categories"
              icon="pi pi-list"
              (click)="showAllCategoriesDialog = true"
              class="all-categories-button"
            ></button>
          </div>

          <!-- Recommended Dropdown -->
          <div class="recommended-container">
            <p-dropdown
              [options]="recommendedOptions"
              [(ngModel)]="selectedRecommended"
              placeholder="Recommended"
              [showClear]="false"
              [appendTo]="'body'"
              class="recommended-dropdown"
            ></p-dropdown>
          </div>
        </div>

        <!-- All Categories Dialog (Desktop) -->
        <p-dialog
          [(visible)]="showAllCategoriesDialog"
          header="Select Category"
          [modal]="true"
          [style]="{ width: '500px' }"
          [draggable]="false"
          [resizable]="false"
        >
          <div class="dialog-content">
            <p-dropdown
              [options]="categoryDropdownOptions"
              [(ngModel)]="selectedCategoryOption"
              placeholder="Select a category"
              [showClear]="true"
              [appendTo]="'body'"
              optionLabel="label"
              optionValue="value"
              (ngModelChange)="onCategorySelectChange($event)"
              class="category-dropdown-full"
              [style]="{ width: '100%' }"
            ></p-dropdown>
          </div>
          <ng-template pTemplate="footer">
            <button
              pButton
              type="button"
              label="Close"
              icon="pi pi-times"
              (click)="showAllCategoriesDialog = false"
              styleClass="p-button-text"
            ></button>
          </ng-template>
        </p-dialog>
      </div>

      <div class="jobs-content">
        <!-- AI Summary -->
        <app-ai-summary [summary]="aiSummary"></app-ai-summary>

        <div class="jobs-header-info">
          <h2 class="jobs-title">Available Jobs</h2>
          <div class="jobs-count" *ngIf="filteredJobs.length > 0">
            {{ filteredJobs.length }} job{{
              filteredJobs.length !== 1 ? 's' : ''
            }}
            found
          </div>
        </div>

        <div class="jobs-grid" *ngIf="filteredJobs.length > 0">
          <app-job-card
            *ngFor="let job of filteredJobs"
            [job]="job"
          ></app-job-card>
        </div>

        <div class="no-jobs" *ngIf="filteredJobs.length === 0 && !loading">
          <div class="no-jobs-content">
            <i class="pi pi-briefcase no-jobs-icon"></i>
            <h3>No jobs found</h3>
            <p *ngIf="searchQuery || selectedCategory">
              No jobs match your search criteria. Try adjusting your search
              terms or selecting a different category.
            </p>
            <p *ngIf="!searchQuery && !selectedCategory">
              There are currently no jobs available.
            </p>
          </div>
        </div>

        <div class="loading" *ngIf="loading">
          <i class="pi pi-spin pi-spinner"></i>
          <span>Loading jobs...</span>
        </div>
      </div>
    </div>
  `,
  styles: [
    `
      .jobs-container {
        width: 100%;
        max-width: 1200px;
        margin: 0 auto;
        padding: 1rem;
        background-color: #f5f5f5;
        min-height: calc(100vh - 60px);
        display: flex;
        flex-direction: column;
        align-items: center;
        box-sizing: border-box;
        overflow-x: hidden;
      }

      .jobs-header {
        width: 100%;
        max-width: 1200px;
        margin-bottom: 1.5rem;
        padding: 1rem;
        background-color: #ffffff;
        border-radius: 8px;
        box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
        box-sizing: border-box;
        overflow-x: hidden;
        position: relative;
        z-index: 1;
      }

      /* Search - Full Width */
      .search-container-full {
        width: 100%;
        margin-bottom: 1rem;
      }

      .search-autocomplete {
        width: 100%;
      }

      /* Categories Row */
      .categories-row {
        display: flex;
        align-items: center;
        gap: 1rem;
        flex-wrap: wrap;
      }

      .recommended-container {
        margin-left: auto;
      }

      .recommended-dropdown {
        min-width: 150px;
      }

      /* Mobile Category Select */
      .category-select-mobile {
        display: none;
        width: 100%;
      }

      .category-dropdown {
        width: 100%;
      }

      /* Desktop Category Boxes */
      .category-boxes-desktop {
        display: flex;
        gap: 1rem;
        flex-wrap: wrap;
        align-items: center;
      }

      .all-categories-button {
        min-width: 150px;
      }

      .category-boxes {
        display: flex;
        gap: 1rem;
        flex-wrap: wrap;
      }

      .category-box {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 1rem;
        padding: 0.75rem 1.25rem;
        background-color: #f5f5f5;
        border: 2px solid transparent;
        border-radius: 8px;
        cursor: pointer;
        transition: all 0.2s ease;
        min-width: 150px;
      }

      .category-box:hover {
        background-color: #e8e8e8;
        border-color: #d0d0d0;
      }

      .category-box.active {
        background-color: #e3f2fd;
        border-color: #4a9eff;
      }

      .category-name {
        font-weight: 600;
        font-size: 0.95rem;
        color: #333333;
      }

      .category-count {
        display: flex;
        align-items: center;
        gap: 0.5rem;
        color: #0066cc;
        font-weight: 500;
        font-size: 0.9rem;
      }

      .category-count i {
        font-size: 0.85rem;
      }

      .jobs-content {
        width: 100%;
        max-width: 1200px;
        box-sizing: border-box;
        overflow-x: hidden;
      }

      .jobs-header-info {
        display: flex;
        justify-content: space-between;
        align-items: center;
        margin-bottom: 1.5rem;
        padding: 0 0.5rem;
        box-sizing: border-box;
        flex-wrap: wrap;
        gap: 0.5rem;
      }

      .jobs-title {
        font-size: 1.75rem;
        font-weight: 600;
        color: #000000;
        margin: 0;
        word-wrap: break-word;
        overflow-wrap: break-word;
        flex: 1;
        min-width: 0;
      }

      .jobs-count {
        font-size: 0.95rem;
        color: #666666;
        white-space: nowrap;
        flex-shrink: 0;
      }

      .jobs-grid {
        display: grid;
        grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
        gap: 1.5rem;
      }

      .no-jobs {
        display: flex;
        justify-content: center;
        align-items: center;
        min-height: 400px;
        padding: 3rem;
      }

      .no-jobs-content {
        text-align: center;
        max-width: 400px;
      }

      .no-jobs-icon {
        font-size: 4rem;
        color: #cccccc;
        margin-bottom: 1rem;
      }

      .no-jobs-content h3 {
        font-size: 1.5rem;
        font-weight: 600;
        color: #000000;
        margin: 0 0 0.5rem 0;
      }

      .no-jobs-content p {
        font-size: 1rem;
        color: #666666;
        margin: 0;
      }

      .loading {
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        gap: 1rem;
        padding: 3rem;
        color: #666666;
      }

      .loading i {
        font-size: 2rem;
      }

      .dialog-content {
        padding: 1rem 0;
      }

      .category-dropdown-full {
        width: 100%;
      }

      /* Responsive: Show mobile dropdown on small screens */
      @media (max-width: 767px) {
        .jobs-container {
          padding: 0.75rem;
          padding-bottom: 4rem; /* Account for bottom navigation */
          min-height: calc(100vh - 50px - 60px); /* Header + bottom nav */
        }

        .jobs-header {
          padding: 0.75rem;
          margin-bottom: 1rem;
        }

        .jobs-content {
          padding-bottom: 1rem;
        }

        .jobs-header-info {
          flex-direction: column;
          align-items: flex-start;
          padding: 0;
        }

        .jobs-title {
          font-size: 1.5rem;
          width: 100%;
        }

        .jobs-count {
          width: 100%;
        }

        .category-select-mobile {
          display: block;
          flex: 1;
          min-width: 0;
        }

        .category-boxes-desktop {
          display: none;
        }

        .recommended-container {
          margin-left: 0;
          width: 100%;
        }

        .categories-row {
          flex-direction: column;
          align-items: stretch;
        }

        .search-container-full {
          width: 100%;
          box-sizing: border-box;
        }
      }

      /* Desktop: Hide mobile dropdown */
      @media (min-width: 768px) {
        .category-select-mobile {
          display: none;
        }

        .category-boxes-desktop {
          display: flex;
        }
      }

      :host ::ng-deep {
        .p-autocomplete {
          width: 100%;
          box-sizing: border-box;
          max-width: 100%;
        }

        .p-autocomplete-input {
          width: 100%;
          max-width: 100%;
          padding: 0.75rem 1rem;
          font-size: 0.95rem;
          box-sizing: border-box;
          word-wrap: break-word;
          overflow-wrap: break-word;
        }

        .p-autocomplete-panel {
          max-width: 100vw;
          box-sizing: border-box;
        }

        .p-dropdown {
          box-sizing: border-box;
          max-width: 100%;
        }

        .p-dropdown-panel {
          z-index: 1000 !important;
        }
      }
    `,
  ],
})
export class JobsComponent implements OnInit {
  allJobs: Job[] = [];
  filteredJobs: Job[] = [];
  searchQuery: string = '';
  searchSuggestions: string[] = [];
  loading = true;
  selectedCategory: string = '';
  categories: CategoryStats[] = [];
  top3Categories: CategoryStats[] = [];
  categoryDropdownOptions: any[] = [];
  selectedCategoryOption: string = '';
  showAllCategoriesDialog: boolean = false;
  aiSummary: string = 'Analyzing your job preferences...';

  recommendedOptions = [
    { label: 'Recommended', value: 'recommended' },
    { label: 'Recent', value: 'recent' },
    { label: 'Popular', value: 'popular' },
  ];
  selectedRecommended = { label: 'Recommended', value: 'recommended' };

  private searchSubject = new Subject<string>();

  constructor(
    private jobService: MockJobService,
    private feedService: MockFeedService
  ) {}

  ngOnInit() {
    this.loadJobs();
    this.generateAISummary();

    // Setup debounced search
    this.searchSubject
      .pipe(debounceTime(300), distinctUntilChanged())
      .subscribe(() => {
        this.filterJobs();
      });
  }

  loadJobs() {
    this.loading = true;
    this.jobService.getAllJobs().subscribe((jobs) => {
      this.allJobs = jobs;
      this.calculateCategoryStats();
      this.filteredJobs = jobs;
      this.loading = false;
    });
  }

  calculateCategoryStats() {
    const categoryCounts: Record<string, number> = {};
    const allCategories = new Set<string>();

    // Collect all unique categories from jobs
    this.allJobs.forEach((job) => {
      allCategories.add(job.category);
      if (!categoryCounts.hasOwnProperty(job.category)) {
        categoryCounts[job.category] = 0;
      }
      categoryCounts[job.category]++;
    });

    // Convert to array and sort by name
    this.categories = Array.from(allCategories)
      .map((category) => ({
        name: category,
        count: categoryCounts[category] || 0,
      }))
      .sort((a, b) => a.name.localeCompare(b.name));

    // Get top 3 categories sorted by count (descending)
    this.top3Categories = [...this.categories]
      .sort((a, b) => b.count - a.count)
      .slice(0, 3);

    // Create dropdown options with category name and count
    this.categoryDropdownOptions = this.categories.map((category) => ({
      label: `${category.name} (${category.count} jobs)`,
      value: category.name,
    }));

    // Add "All Categories" option at the beginning
    this.categoryDropdownOptions.unshift({
      label: 'All Categories',
      value: '',
    });

    // Initialize selected category option (value is the category name string)
    this.selectedCategoryOption = this.selectedCategory || '';
  }

  searchAutocomplete(event: any) {
    const query = event.query.toLowerCase();
    if (query.length < 2) {
      this.searchSuggestions = [];
      return;
    }

    // Generate suggestions from job titles, companies, and locations
    const suggestions = new Set<string>();
    this.allJobs.forEach((job) => {
      if (job.title.toLowerCase().includes(query)) {
        suggestions.add(job.title);
      }
      if (job.company.toLowerCase().includes(query)) {
        suggestions.add(job.company);
      }
      if (job.location.toLowerCase().includes(query)) {
        suggestions.add(job.location);
      }
    });

    this.searchSuggestions = Array.from(suggestions).slice(0, 10);

    // Trigger search filter
    this.searchSubject.next(query);
  }

  onSearchSelect(event: AutoCompleteSelectEvent) {
    const selected = event.value;
    if (!selected) {
      this.clearSearch();
      return;
    }

    this.searchQuery = selected;
    this.selectedCategory = ''; // Clear category when searching
    this.filterJobs();
  }

  onSearchInput() {
    // Trigger search when user types
    this.searchSubject.next(this.searchQuery);
  }

  onCategorySelectChange(categoryName: string) {
    // ngModelChange passes the value directly (category name string) when using optionValue
    this.selectCategory(categoryName || '');
    // Close dialog if open (desktop)
    if (this.showAllCategoriesDialog) {
      this.showAllCategoriesDialog = false;
    }
  }

  onCategoryClick(categoryName: string) {
    // Toggle category selection
    if (this.selectedCategory === categoryName) {
      this.selectCategory('');
      // Update dropdown selection
      this.selectedCategoryOption = '';
    } else {
      this.selectCategory(categoryName);
      // Update dropdown selection (value is the category name string)
      this.selectedCategoryOption = categoryName;
    }
  }

  private selectCategory(categoryName: string) {
    // Clear search if active
    if (!categoryName) {
      this.selectedCategory = '';
      this.selectedCategoryOption = ''; // Keep dropdown in sync
      this.searchQuery = '';
      this.filteredJobs = this.allJobs;
      return;
    }

    this.selectedCategory = categoryName;
    this.selectedCategoryOption = categoryName; // Keep dropdown in sync
    this.searchQuery = ''; // Clear search when selecting category
    this.filterJobs();
  }

  filterJobs() {
    let jobs = [...this.allJobs];

    // Filter by category if selected
    if (this.selectedCategory) {
      jobs = jobs.filter((job) => job.category === this.selectedCategory);
    }

    // Filter by search query if provided
    if (this.searchQuery && this.searchQuery.trim() !== '') {
      const query = this.searchQuery.toLowerCase().trim();
      jobs = jobs.filter(
        (job) =>
          job.title.toLowerCase().includes(query) ||
          job.company.toLowerCase().includes(query) ||
          job.location.toLowerCase().includes(query) ||
          job.category.toLowerCase().includes(query) ||
          job.description.toLowerCase().includes(query)
      );
    }

    this.filteredJobs = jobs;
  }

  clearSearch() {
    this.searchQuery = '';
    this.selectedCategory = '';
    this.selectedCategoryOption = '';
    this.filteredJobs = this.allJobs;
  }

  generateAISummary() {
    this.feedService.getLikedPosts().subscribe((likedPosts) => {
      if (likedPosts.length === 0) {
        this.aiSummary =
          "Start liking posts to get personalized job category recommendations! Like posts that interest you, and I'll analyze your preferences to suggest the best job categories for you.";
        return;
      }

      // Count categories from liked posts
      const categoryCounts: Record<string, number> = {};
      likedPosts.forEach((post) => {
        post.categories.forEach((category) => {
          categoryCounts[category] = (categoryCounts[category] || 0) + 1;
        });
      });

      // Sort categories by count
      const sortedCategories = Object.entries(categoryCounts)
        .sort((a, b) => b[1] - a[1])
        .map(([category]) => category);

      if (sortedCategories.length === 0) {
        this.aiSummary =
          "Start liking posts to get personalized job category recommendations! Like posts that interest you, and I'll analyze your preferences to suggest the best job categories for you.";
        return;
      }

      // Get top category
      const topCategory = sortedCategories[0];
      const topCount = categoryCounts[topCategory];

      // Generate recommendation message
      if (sortedCategories.length === 1) {
        this.aiSummary = `Based on your ${likedPosts.length} liked post${
          likedPosts.length > 1 ? 's' : ''
        }, you're most interested in ${topCategory} content. I recommend exploring ${topCategory} jobs as they align perfectly with your interests!`;
      } else if (sortedCategories.length === 2) {
        const secondCategory = sortedCategories[1];
        this.aiSummary = `Based on your ${likedPosts.length} liked post${
          likedPosts.length > 1 ? 's' : ''
        }, you show strong interest in ${topCategory} (${topCount} liked) and ${secondCategory} (${
          categoryCounts[secondCategory]
        } liked). I recommend focusing on ${topCategory} jobs, but also explore ${secondCategory} opportunities that match your preferences.`;
      } else {
        const topCategories = sortedCategories.slice(0, 3).join(', ');
        this.aiSummary = `Based on your ${likedPosts.length} liked post${
          likedPosts.length > 1 ? 's' : ''
        }, you're interested in multiple categories: ${topCategories}. Your top interest is ${topCategory} with ${topCount} liked post${
          topCount > 1 ? 's' : ''
        }. I recommend exploring more ${topCategory} positions, as they seem to be the best match for your career interests!`;
      }
    });
  }
}
